import { Component, output, signal, inject, DestroyRef, viewChild, ElementRef, effect, input } from '@angular/core'
import { CommonModule } from '@angular/common'
import { FormControl, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms'
import { MarkdownModule } from 'ngx-markdown'
import { FaIconComponent } from '@fortawesome/angular-fontawesome'
import { AiPromptService, AiMode } from 'src/app/core/services/ai/ai-prompt.service'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { ButtonComponent } from '../form-elements/button/button.component'
import { AppStore } from 'src/app/core/app.store'
import { NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap'
import { AiChatPanelService } from 'src/app/core/services/ai/ai-chat-panel.service'

@Component({
    selector: 'sf-ai-chat-panel',
    imports: [
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        MarkdownModule,
        FaIconComponent,
        ButtonComponent,
        NgbTooltipModule,
    ],
    templateUrl: './ai-chat-panel.component.html',
    styleUrl: './ai-chat-panel.component.scss',
})
export class AiChatPanelComponent {
    closed = output<void>()
    initialPrompt = input<string | null>(null)

    #appStore = inject(AppStore)
    #aiChatPanelService = inject(AiChatPanelService)

    me = this.#appStore.me

    #aiService = inject(AiPromptService)
    #destroyRef = inject(DestroyRef)

    promptControl = new FormControl('', { nonNullable: true, validators: [Validators.required] })
    isLoading = signal(false)
    messages = this.#aiService.conversationHistory
    usageTokens = this.#aiService.usageTokens
    currentMode = this.#aiService.mode

    AiMode = AiMode

    messagesContainer = viewChild<ElementRef<HTMLDivElement>>('messagesContainer')

    panelWidth = signal(600)
    #isResizing = false
    #startX = 0
    #startWidth = 0

    chatWelcomeMessage = `## 👋 Welcome to your AI Assistant!

I'm here to help you manage your business more efficiently. I can assist you with:

- 📅 **Appointments** - View schedules, check availability
- 👥 **Staff Management** - Find available engineers, check workloads
- 📊 **Analytics** - Get insights on appointment patterns and performance
- 🔍 **Quick Searches** - Find information across your system
- 📋 **Docs** - Ask how to do something like 'How do i add a customer'

Try asking me something or use the quick action buttons below!`

    boilerWelcomeMessage = `## 🔥 Welcome to Boiler Copilot!

I'm your specialist assistant for all things boiler-related. I can help you with:

- 🔧 **Installation Guidance** - Requirements, specifications, and best practices
- 🛠️ **Service Information** - Maintenance schedules and service procedures
- ⚠️ **Troubleshooting** - Diagnose common issues and find solutions
- 📖 **Technical Knowledge** - Manufacturer-specific information and guidelines

Ask me about any boiler make, model or GC Number, or use the quick actions below!`

    constructor() {
        // Auto-scroll to bottom when new messages arrive
        effect(() => {
            const msgs = this.messages()
            if (msgs.length > 0) {
                setTimeout(() => this.#scrollToBottom(), 100)
            }
        })

        // Handle initial prompt from service
        effect(() => {
            const prompt = this.initialPrompt()
            if (prompt) {
                this.clearHistory()
                this.promptControl.setValue(prompt)
                this.#aiChatPanelService.clearInitialPrompt()
            }
        })
    }

    close() {
        this.closed.emit()
    }

    sendPrompt() {
        if (this.promptControl.invalid || this.isLoading()) return

        const prompt = this.promptControl.value.trim()
        if (!prompt) return

        this.#executePrompt(prompt)
        this.promptControl.reset()
    }

    sendQuickPrompt(prompt: string) {
        if (this.isLoading()) return
        this.#executePrompt(prompt)
    }

    #executePrompt(prompt: string) {
        this.isLoading.set(true)
        this.promptControl.disable()

        this.#aiService
            .sendPrompt(prompt)
            .pipe(takeUntilDestroyed(this.#destroyRef))
            .subscribe({
                next: () => {
                    // Chunks are automatically added to the message by the service
                },
                complete: () => {
                    this.isLoading.set(false)
                    this.promptControl.enable()
                },
                error: (error) => {
                    console.error('AI Error:', error)
                    this.isLoading.set(false)
                    this.promptControl.enable()
                    // Optionally show error in chat
                },
            })
    }

    clearHistory() {
        this.#aiService.clearHistory()
        this.promptControl.reset()
    }

    switchMode(mode: AiMode) {
        this.#aiService.setMode(mode)
    }

    #scrollToBottom() {
        const container = this.messagesContainer()?.nativeElement
        if (container) {
            container.scrollTop = container.scrollHeight
        }
    }

    startResize(event: MouseEvent) {
        event.preventDefault()
        this.#isResizing = true
        this.#startX = event.clientX
        this.#startWidth = this.panelWidth()

        const handleMouseMove = (e: MouseEvent) => {
            if (!this.#isResizing) return

            const deltaX = this.#startX - e.clientX
            const newWidth = Math.max(300, Math.min(window.innerWidth * 0.9, this.#startWidth + deltaX))
            this.panelWidth.set(newWidth)
        }

        const handleMouseUp = () => {
            this.#isResizing = false
            document.removeEventListener('mousemove', handleMouseMove)
            document.removeEventListener('mouseup', handleMouseUp)
        }

        document.addEventListener('mousemove', handleMouseMove)
        document.addEventListener('mouseup', handleMouseUp)
    }
}
